// 任务 API 封装 / Tasks API facade.
import { api, type ApiClient } from "@/api/client";
import type {
  Mode,
  SimulationConfig,
  SimulationStatus,
  TaskDetail,
  TaskListItem,
  TaskReportSummary,
} from "@/lib/types";

// 创建任务请求 / Request body for creating a task.
export interface TaskCreateRequest {
  mode: Mode;
  title?: string;
  plan_a_md?: string | null;
  plan_b_md?: string | null;
  config?: Partial<SimulationConfig>;
}

// 更新任务请求 / Request body for updating a task.
export interface TaskUpdateRequest {
  title?: string | null;
  plan_a_md?: string | null;
  plan_b_md?: string | null;
  config?: SimulationConfig | null;
}

// 任务列表响应 / Task list response.
export interface TaskListResponse {
  ok: true;
  tasks: TaskListItem[];
}

// 任务详情响应 / Task detail response.
export interface TaskDetailResponse {
  ok: true;
  task: TaskDetail;
}

// 任务启动模拟响应 / Task simulation start response.
export interface TaskSimulationStartResponse {
  ok: true;
  simulation_id: string;
  status: SimulationStatus;
  events_url: string;
}

// 任务报告列表响应 / Task reports response.
export interface TaskReportsResponse {
  ok: true;
  reports: TaskReportSummary[];
}

// 任务删除响应 / Task delete response.
export interface TaskDeleteResponse {
  ok: true;
  deleted: true;
}

/**
 * 构造任务 API 方法集 / Build the task API method set.
 */
export function createTasksApi(client: ApiClient) {
  return {
    list: (mode?: Mode): Promise<TaskListResponse> =>
      client.get<TaskListResponse>(
        mode ? `/api/v1/tasks?mode=${encodeURIComponent(mode)}` : "/api/v1/tasks",
      ),

    create: (body: TaskCreateRequest): Promise<TaskDetailResponse> =>
      client.post<TaskDetailResponse>("/api/v1/tasks", body),

    get: (id: string): Promise<TaskDetailResponse> =>
      client.get<TaskDetailResponse>(`/api/v1/tasks/${encodeURIComponent(id)}`),

    update: (id: string, body: TaskUpdateRequest): Promise<TaskDetailResponse> =>
      client.patch<TaskDetailResponse>(`/api/v1/tasks/${encodeURIComponent(id)}`, body),

    startSimulation: (id: string): Promise<TaskSimulationStartResponse> =>
      client.post<TaskSimulationStartResponse>(
        `/api/v1/tasks/${encodeURIComponent(id)}/simulations`,
        {},
      ),

    reports: (id: string): Promise<TaskReportsResponse> =>
      client.get<TaskReportsResponse>(`/api/v1/tasks/${encodeURIComponent(id)}/reports`),

    delete: (id: string): Promise<TaskDeleteResponse> =>
      client.delete<TaskDeleteResponse>(`/api/v1/tasks/${encodeURIComponent(id)}`),
  };
}

// 默认单例 / Default singleton bound to the shared ApiClient.
export const tasksApi = createTasksApi(api);
